import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// Fleet registers (Driver Licence Checks, Fines & PCNs, Rental Van
// Inspections), kept on the tech server; the dashboard forwards to it and tech
// applies the Fleet gate. The fields, stages and file types of each register
// come from the server's config, so this page draws whatever it describes.

export function useFleetRegisterConfig() {
  return useQuery({
    queryKey: ['fleetRegisterConfig'],
    queryFn: async () => (await api.get('/api/fleet-registers/config')).data.registers,
    staleTime: 10 * 60 * 1000,
  });
}

export function useFleetRegisterList(register, params) {
  return useQuery({
    queryKey: ['fleetRegister', register, params],
    queryFn: async () => (await api.get(`/api/fleet-registers/${register}`, { params })).data,
    placeholderData: (prev) => prev, // no flash of "Loading…" between pages
    staleTime: 30000,
  });
}

export function useFleetRecord(register, id) {
  return useQuery({
    queryKey: ['fleetRecord', register, id],
    queryFn: async () => (await api.get(`/api/fleet-registers/${register}/${id}`)).data,
    enabled: Boolean(id),
    staleTime: 0,
  });
}

function useInvalidate(register) {
  const qc = useQueryClient();
  return (id) => {
    qc.invalidateQueries({ queryKey: ['fleetRegister', register] });
    if (id) qc.invalidateQueries({ queryKey: ['fleetRecord', register, id] });
  };
}

export function useSaveFleetRecord(register) {
  const invalidate = useInvalidate(register);
  return useMutation({
    mutationFn: async ({ id, ...body }) => (id
      ? (await api.patch(`/api/fleet-registers/${register}/${id}`, body)).data
      : (await api.post(`/api/fleet-registers/${register}`, body)).data),
    onSuccess: (r, v) => invalidate(v.id || r.id),
  });
}

export function useAddFleetNote(register, id) {
  const invalidate = useInvalidate(register);
  return useMutation({
    mutationFn: async (body) => (await api.post(`/api/fleet-registers/${register}/${id}/notes`, { body })).data,
    onSuccess: () => invalidate(id),
  });
}

export function useUploadFleetFile(register, id) {
  const invalidate = useInvalidate(register);
  return useMutation({
    mutationFn: async ({ kind, name, contentBase64 }) => (await api.post(`/api/fleet-registers/${register}/${id}/files`, { kind, name, contentBase64 })).data,
    onSuccess: () => invalidate(id),
  });
}

// A stored file as a blob URL (the API needs the sign-in token, so a plain
// <img src> cannot fetch it).
export async function fetchFleetFile(register, id, fileId, thumb) {
  const { data } = await api.get(`/api/fleet-registers/${register}/${id}/files/${fileId}`, {
    params: thumb ? { thumb: 1 } : {}, responseType: 'blob',
  });
  return URL.createObjectURL(data);
}
